// SPDX-License-Identifier: AGPL-3.0-or-later

import {VOICE_ROOM_AMBIENCE as AMBIENCE_MANIFEST} from '@fluxer/constants/src/VoiceRoomAmbienceManifest';
import type {VoiceRoomTheme} from '@fluxer/constants/src/VoiceRoomThemes';

// The room sound set (mono Opus 48 kHz; CC0, and Micup's own ElevenLabs output, sources in the room repo's
// sesler/LISANS.txt), served next to the room files under the static CDN's /rooms/.
// Names carry a content hash, so they are cached immutably; a new sound is a new name.

export interface VoiceRoomSound {
	readonly file: string;
	// Linear gain on top of the file's own level, 0 to 1.
	readonly gain: number;
}

export const VOICE_ROOM_SFX = {
	join: {file: 'giris.ea99605cc550.ogg', gain: 1},
	leave: {file: 'cikis.1147350253fc.ogg', gain: 1},
	sit: {file: 'otur.11b8d753c594.ogg', gain: 1},
	stand: {file: 'kalk.827850df21fb.ogg', gain: 1},
	wave: {file: 'el-salla.e9e04c767574.ogg', gain: 1},
	reaction: {file: 'tepki.234ae6dafcf0.ogg', gain: 1},
	slap: {file: 'tokat.73c8d54ad564.ogg', gain: 1},
	step_wood_1: {file: 'adim-ahsap-1.0df55362c36a.ogg', gain: 1},
	step_wood_2: {file: 'adim-ahsap-2.a172d11936d6.ogg', gain: 1},
	step_wood_3: {file: 'adim-ahsap-3.37686e9662d7.ogg', gain: 1},
	step_wood_4: {file: 'adim-ahsap-4.6bc494e56c17.ogg', gain: 1},
	step_rug_1: {file: 'adim-hali-1.52160722d18c.ogg', gain: 1},
	step_rug_2: {file: 'adim-hali-2.b774cc4d6ac3.ogg', gain: 1},
	step_grass_1: {file: 'adim-cim-1.4d06c5824166.ogg', gain: 1},
	step_grass_2: {file: 'adim-cim-2.206a2b369d00.ogg', gain: 1},
	step_grass_3: {file: 'adim-cim-3.54e9f5be6533.ogg', gain: 1},
	step_grass_4: {file: 'adim-cim-4.0318bcf82018.ogg', gain: 1},
	step_dirt_1: {file: 'adim-toprak-1.cf9e5b4521ea.ogg', gain: 1},
	step_dirt_2: {file: 'adim-toprak-2.4f8fe7c27333.ogg', gain: 1},
	step_dirt_3: {file: 'adim-toprak-3.bf89279eb643.ogg', gain: 1},
	step_dirt_4: {file: 'adim-toprak-4.65bc0a25281f.ogg', gain: 1},
	step_cinema_carpet_1: {file: 'adim-sinema-hali-1.0e758467b45c.ogg', gain: 1},
	step_cinema_carpet_2: {file: 'adim-sinema-hali-2.e7be96d1be47.ogg', gain: 1},
	step_cinema_carpet_3: {file: 'adim-sinema-hali-3.b5e46c4e7b36.ogg', gain: 1},
	step_cinema_carpet_4: {file: 'adim-sinema-hali-4.ed42f1de81b9.ogg', gain: 1},
	seat_cinema_1: {file: 'koltuk-1.56da2cb913d5.ogg', gain: 1},
	seat_cinema_2: {file: 'koltuk-2.6eb78b68e29f.ogg', gain: 1},
	seat_cinema_3: {file: 'koltuk-3.dce7e32e151b.ogg', gain: 1},
	log_crack_1: {file: 'odun-citirti-1.c9f007dd0f9a.ogg', gain: 1},
	log_crack_2: {file: 'odun-citirti-2.22bcb8a315ca.ogg', gain: 1},
	log_crack_3: {file: 'odun-citirti-3.de933f8d2fad.ogg', gain: 1},
} as const satisfies Record<string, VoiceRoomSound>;

export type VoiceRoomSfx = keyof typeof VOICE_ROOM_SFX;

// By room theme id; a room without one plays no ambience loop.
export const VOICE_ROOM_AMBIENCE: Readonly<Partial<Record<VoiceRoomTheme, VoiceRoomSound>>> = AMBIENCE_MANIFEST;

// A point of the room GLB (an empty ses_<name> node) a sound comes from: full level within refDistance, silent from
// maxDistance, linear in between (metres, to the listener's ears).
export interface VoiceRoomSoundPoint {
	readonly node: string;
	readonly refDistance: number;
	readonly maxDistance: number;
}

// A loop playing at its point, on top of the room's ambience (its volume, ducking and deafen).
export interface VoiceRoomEmitter extends VoiceRoomSoundPoint {
	readonly sound: VoiceRoomSound;
}

// One of the sounds every minS–maxS seconds, at its point or, without one, from a random direction.
export interface VoiceRoomOneshots {
	readonly sounds: ReadonlyArray<VoiceRoomSfx>;
	readonly minS: number;
	readonly maxS: number;
	readonly at?: VoiceRoomSoundPoint;
}

export interface VoiceRoomSceneSounds {
	readonly emitters?: ReadonlyArray<VoiceRoomEmitter>;
	readonly oneshots?: ReadonlyArray<VoiceRoomOneshots>;
	// Footsteps in turn; inside a zone (floor distance to its node) the zone's. Without it the room walks on wood.
	readonly steps?: {
		readonly sounds: ReadonlyArray<VoiceRoomSfx>;
		readonly zones?: ReadonlyArray<{
			readonly node: string;
			readonly radius: number;
			readonly sounds: ReadonlyArray<VoiceRoomSfx>;
		}>;
	};
	// Sitting down plays these in turn instead of sit.
	readonly sit?: ReadonlyArray<VoiceRoomSfx>;
}

const CAMP_FIRE = {node: 'ses_ates', refDistance: 2.5, maxDistance: 12} as const;

// By room theme id.
export const VOICE_ROOM_SCENE_SOUNDS: Readonly<Partial<Record<VoiceRoomTheme, VoiceRoomSceneSounds>>> = {
	'kamp-atesi': {
		emitters: [{...CAMP_FIRE, sound: {file: 'kamp-ates.13332736a310.ogg', gain: 1}}],
		oneshots: [{sounds: ['log_crack_1', 'log_crack_2', 'log_crack_3'], minS: 8, maxS: 25, at: CAMP_FIRE}],
		steps: {
			sounds: ['step_grass_1', 'step_grass_2', 'step_grass_3', 'step_grass_4'],
			zones: [{node: CAMP_FIRE.node, radius: 4, sounds: ['step_dirt_1', 'step_dirt_2', 'step_dirt_3', 'step_dirt_4']}],
		},
	},
	sinema: {
		steps: {
			sounds: ['step_cinema_carpet_1', 'step_cinema_carpet_2', 'step_cinema_carpet_3', 'step_cinema_carpet_4'],
		},
		sit: ['seat_cinema_1', 'seat_cinema_2', 'seat_cinema_3'],
	},
};

// Whether the room plays anything on the room volume: an ambience loop, a point loop or now-and-then sounds.
export function voiceRoomHasAmbience(theme: VoiceRoomTheme): boolean {
	const scene = VOICE_ROOM_SCENE_SOUNDS[theme];
	return Boolean(VOICE_ROOM_AMBIENCE[theme] || scene?.emitters?.length || scene?.oneshots?.length);
}
